/* ============================================================
   Cardio Online — automatic delivery quote (shared engine)

   Powers the "Delivery to Brisbane, 4000 — $31.11" line that fills
   itself in on the store PDP and on the ON:PACE product pages. Both
   templates keep their own markup and their own styling; this file
   only resolves WHERE the visitor is and WHAT the freight costs, and
   hands both back.

   WHY THIS DOES NOT REUSE THE EXISTING CALCULATORS' QUOTING
   --------------------------------------------------------
   co-ship-calc.js and onpace-shipcalc.js quote by putting the item in
   the customer's cart, asking, and taking it back out. That is fine
   for a button the customer chose to press. It is NOT fine on page
   load: /cart/add fires Shopify's `product_added_to_cart` event, which
   feeds Meta, GA4 and Klaviyo. Auto-quoting through it would stamp an
   AddToCart on every product view -- dissolving the Meta BOF audiences
   (built on ATC/IC 60D), inflating GA4 add_to_cart, and mailing
   abandoned-cart to people who only browsed. Measured on the live
   storefront 2026-09-10: one monorail added_to_cart per quote.

   So this quotes through the Storefront API instead. cartCreate makes
   a throwaway cart object that is not the customer's cart, is never
   shown to them, and emits no cart events. Verified to return numbers
   identical to the cart method ($65.98 to 4551 by both routes).

   WHAT IS TRUSTED, AND WHAT IS A GUESS
   ------------------------------------
   Only a postcode the customer gave us is treated as settled. An
   IP-derived one is always flagged `uncertain`, because AU IP geo is
   not good enough to state a freight price as fact -- the free public
   APIs returned a GPO box (4001) and an invalid postcode (9015) for a
   Brisbane IP on 2026-09-10. Freight on one dumbbell ranges $14.30
   (Sydney) to $65.98 (Sunshine Coast), so a wrong guess presented as
   fact is a checkout-abandonment problem, not a cosmetic one. The
   consuming template uses the flag to decide whether to say "Delivery
   to X" or "Delivery to X -- not your area?".

   Only the postcode is actually needed for a rate (verified: postcode
   alone quotes the same as a full street address). Suburb and state
   are carried for DISPLAY, so the customer can see a wrong guess.
   ============================================================ */
(function () {
  'use strict';
  if (window.CosShipAuto) return;

  var CFG = (function () {
    var el = document.getElementById('cos-ship-auto-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  })();

  var LOC_KEY  = 'cos-ship-location';
  var LOC_TTL  = 30 * 24 * 60 * 60 * 1000;   /* same order as Koala's cache */
  var RATE_TTL = 60 * 60 * 1000;

  /* ---------- storage -------------------------------------------------
     Every read and write is wrapped: a private window, cleared site data
     or a browser set to block storage makes the accessor itself throw,
     and a thrown quote is a blank line on a product page. */
  function readJSON(store, key, ttl) {
    try {
      var raw = window[store].getItem(key);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || typeof v !== 'object') return null;
      if (ttl && (!v.ts || Date.now() - v.ts > ttl)) { window[store].removeItem(key); return null; }
      return v;
    } catch (e) { return null; }
  }

  function writeJSON(store, key, value) {
    try {
      value.ts = Date.now();
      window[store].setItem(key, JSON.stringify(value));
    } catch (e) { /* not worth breaking the page over */ }
  }

  function rateKey(variantId, zip) { return 'cos-ship-rate:' + variantId + ':' + zip; }

  function validPostcode(z) { return /^\d{4}$/.test(String(z || '')); }

  /* ---------- location ------------------------------------------------ */
  var locPromise = null;

  function resolveLocation() {
    if (locPromise) return locPromise;

    /* 1. What the customer last told us, on this device. */
    var stored = readJSON('localStorage', LOC_KEY, LOC_TTL);
    if (stored && validPostcode(stored.postcode)) {
      locPromise = Promise.resolve(stored);
      return locPromise;
    }

    /* 2. A logged-in customer's own default address. */
    if (CFG && CFG.customer && validPostcode(CFG.customer.postcode)) {
      var acct = {
        postcode: CFG.customer.postcode,
        suburb: CFG.customer.suburb || '',
        state: CFG.customer.state || '',
        uncertain: false,
        source: 'account'
      };
      writeJSON('localStorage', LOC_KEY, acct);
      locPromise = Promise.resolve(acct);
      return locPromise;
    }

    /* 3. Cloudflare's edge geo. One hop per visitor per 30 days -- the
          result is cached above, guess and all, so this is not a
          per-pageview request. */
    if (!CFG || !CFG.geoUrl) { locPromise = Promise.resolve(null); return locPromise; }

    locPromise = fetch(CFG.geoUrl, { mode: 'cors', credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (g) {
        if (!g || g.country !== 'AU' || !validPostcode(g.postcode)) return null;
        var loc = {
          postcode: g.postcode,
          suburb: g.suburb || '',
          state: g.state || '',
          stateCode: g.stateCode || '',
          uncertain: true,
          source: 'ip'
        };
        writeJSON('localStorage', LOC_KEY, loc);
        return loc;
      })
      .catch(function () { return null; });

    return locPromise;
  }

  /* Called when the customer types a postcode themselves. That answer
     outranks anything we inferred, so it is stored as settled and the
     resolved promise is replaced. */
  function setLocation(loc) {
    if (!loc || !validPostcode(loc.postcode)) return null;
    var settled = {
      postcode: String(loc.postcode),
      suburb: loc.suburb || '',
      state: loc.state || '',
      uncertain: false,
      source: 'customer'
    };
    writeJSON('localStorage', LOC_KEY, settled);
    locPromise = Promise.resolve(settled);
    return settled;
  }

  /* ---------- Storefront API ------------------------------------------ */
  var CREATE =
    'mutation($l:[CartLineInput!]!,$a:MailingAddressInput!){' +
    'cartCreate(input:{lines:$l,buyerIdentity:{countryCode:AU,' +
    'deliveryAddressPreferences:[{deliveryAddress:$a}]}}){cart{id}userErrors{message}}}';

  /* withCarrierRates is refused unless the field is deferred -- the
     carrier apps are the slow part, and Shopify streams them in a
     second multipart chunk once they answer. */
  var RATES =
    'query($id:ID!){cart(id:$id){...on Cart @defer{' +
    'deliveryGroups(first:5,withCarrierRates:true){edges{node{deliveryOptions{' +
    'title code estimatedCost{amount currencyCode}}}}}}}}';

  function parseParts(text, contentType) {
    var boundary = 'graphql';
    var m = /boundary=([^;]+)/i.exec(contentType || '');
    if (m) boundary = m[1].trim().replace(/^"|"$/g, '');
    var out = [];
    text.split('--' + boundary).forEach(function (chunk) {
      var i = chunk.indexOf('\r\n\r\n');
      if (i < 0) return;
      var body = chunk.slice(i + 4).trim();
      if (!body || body === '--') return;
      try { out.push(JSON.parse(body)); } catch (e) { /* boundary noise */ }
    });
    return out;
  }

  function sfetch(query, variables) {
    return fetch('/api/' + CFG.apiVersion + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': CFG.token
      },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (r) {
      var ct = r.headers.get('content-type') || '';
      return r.text().then(function (t) {
        if (ct.indexOf('multipart/mixed') > -1) return parseParts(t, ct);
        try { return [JSON.parse(t)]; } catch (e) { return []; }
      });
    });
  }

  function cheapestOption(parts) {
    var options = [];
    parts.forEach(function (p) {
      var blocks = [];
      if (p.data && p.data.cart) blocks.push(p.data.cart);
      if (p.incremental) p.incremental.forEach(function (i) { if (i.data) blocks.push(i.data); });
      blocks.forEach(function (b) {
        var edges = b.deliveryGroups && b.deliveryGroups.edges;
        if (!edges) return;
        edges.forEach(function (e) {
          (e.node.deliveryOptions || []).forEach(function (o) {
            var amt = parseFloat(o.estimatedCost && o.estimatedCost.amount);
            if (!isNaN(amt)) options.push({ title: o.title, code: o.code, amount: amt });
          });
        });
      });
    });
    if (!options.length) return null;
    options.sort(function (a, b) { return a.amount - b.amount; });

    /* NOT simply the cheapest. Bulky items come back with a depot option
       alongside the real one, and the depot option is always cheaper --
       on the SOYO 5 to Perth it is $171.25 against $267.50 for home
       delivery, and to Hobart $136.50 against $290.00. Printing the
       depot price under a label that reads "Delivery to Perth" tells
       the customer a number they cannot actually have delivered, and
       they find the true one at checkout. Quote what the label claims:
       delivery. Someone who would rather collect can still pick the
       cheaper option at checkout and be pleasantly surprised, which is
       the right way round. */
    var delivered = options.filter(function (o) {
      return !/depot|collect|pick\s*-?\s*up/i.test(o.title || '');
    });
    return (delivered.length ? delivered : options)[0];
  }

  var inFlight = {};

  /* Resolves to {title, amount} | null. `null` means "the carriers
     returned nothing for this item" -- a real answer, not an error, and
     the caller shows nothing rather than an error banner. Rejects only
     when the lookup itself failed. */
  function quote(variantId, loc) {
    if (!CFG || !CFG.token) return Promise.reject(new Error('no-storefront-token'));
    if (!variantId || !loc || !validPostcode(loc.postcode)) return Promise.reject(new Error('bad-input'));

    var key = rateKey(variantId, loc.postcode);
    var cached = readJSON('sessionStorage', key, RATE_TTL);
    if (cached) return Promise.resolve(cached.amount === null ? null : cached);

    if (inFlight[key]) return inFlight[key];

    var address = { country: 'Australia', zip: String(loc.postcode) };
    if (loc.state)  address.province = loc.state;
    if (loc.suburb) address.city = loc.suburb;

    inFlight[key] = sfetch(CREATE, {
      l: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: 1 }],
      a: address
    })
      .then(function (parts) {
        var created = parts[0] && parts[0].data && parts[0].data.cartCreate;
        var cart = created && created.cart;
        if (!cart || !cart.id) throw new Error('cart-create-failed');
        return sfetch(RATES, { id: cart.id });
      })
      .then(function (parts) {
        var best = cheapestOption(parts);
        writeJSON('sessionStorage', key, best ? { title: best.title, amount: best.amount }
                                              : { amount: null });
        return best;
      })
      .then(function (v) { delete inFlight[key]; return v; },
            function (e) { delete inFlight[key]; throw e; });

    return inFlight[key];
  }

  /* ---------- shared helpers for the two templates -------------------- */

  /* "Brisbane, 4000" — suburb when we have one, postcode alone when not. */
  function placeLabel(loc) {
    if (!loc) return '';
    return loc.suburb ? loc.suburb + ', ' + loc.postcode : String(loc.postcode);
  }

  /* Run once the page has settled. Deliberately NOT on `load`: that waits
     on every gallery image and would push the line out past 7s on a PDP.
     DOMContentLoaded + idle keeps it off the critical path while still
     filling in quickly. Nothing here is render-blocking -- the whole
     round trip is ~200 bytes and one JSON parse. */
  function onIdle(fn) {
    var ran = false;
    function once() { if (ran) return; ran = true; fn(); }

    function schedule() {
      /* A HIDDEN document never runs an idle callback -- and does not
         honour its timeout either, so requestIdleCallback alone is not
         the guarantee it looks like. Opening a product in a background
         tab is ordinary behaviour (middle-click from a collection), so
         wait for the visit to actually happen rather than quoting
         freight for someone who is not looking. Verified 2026-09-10:
         rIC with timeout:2000 did not fire in 3s while hidden. */
      if (document.hidden) {
        document.addEventListener('visibilitychange', function onVis() {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', onVis);
          schedule();
        });
        return;
      }
      /* Visible: let a busy main thread finish first, but keep a plain
         timer as the actual guarantee. */
      if (window.requestIdleCallback) window.requestIdleCallback(once, { timeout: 1500 });
      setTimeout(once, 1500);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
    else schedule();
  }

  /* The buy form is the only reliable source of the selected variant --
     both PDP templates write it there, and the block's own attribute is
     only a fallback (it renders empty on some live pages). */
  function currentVariantId(formSelector, root) {
    var input = (formSelector && document.querySelector(formSelector)) ||
                document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return String(input.value);
    var attr = root && root.getAttribute('data-variant-id');
    return attr || null;
  }

  /* Variant swaps emit no standard event in either template, so watch the
     interactions that cause them and re-read the form. Cached rates make
     the re-quote free for a postcode already seen. */
  function onVariantChange(formSelector, root, cb) {
    var last = currentVariantId(formSelector, root);
    function check() {
      var now = currentVariantId(formSelector, root);
      if (now && now !== last) { last = now; cb(now); }
    }
    document.addEventListener('change', function () { setTimeout(check, 60); }, true);
    document.addEventListener('click', function () { setTimeout(check, 220); }, true);
  }

  /* ---------- painting the answer -------------------------------------
     BOTH templates keep their existing single-line toggle and this only
     REPLACES ITS LABEL: "Calculate shipping" becomes "Delivery to
     Richmond, 3121 — $31.11". Nothing is inserted into the flow, the
     element does not change height, and the label is held to one line
     in CSS, so filling it in shifts nothing. That matters more than the
     styling here -- this theme has been through a CLS optimisation pass
     and a block that grows a line 3s after paint would undo it.

     An uncertain (IP-derived) postcode is prefixed "Est." rather than
     given a trailing hedge, so the qualifier survives truncation on a
     narrow phone instead of being the first thing ellipsed away. */

  /* The line is three text nodes inside markup Liquid already rendered:
     lead ("Est. delivery to" / "Delivery to"), place (a button), price. This
     only rewrites their text -- it never builds or removes an element -- so
     the answer arriving cannot shift the page, and the resting state
     ("Est. delivery to Select your postcode — $ TBC") is a real fallback
     rather than a blank waiting to be filled.

     `rate` may be null: we usually know WHERE before we know how much, and a
     place name on screen while the carrier is still thinking is better than a
     stale prompt. The price simply keeps whatever it had until a figure lands.

     The place name is the part allowed to give way when the line is too
     narrow; the price is never allowed to shrink. Splitting them is what
     stops the ellipsis eating the only number anyone opened the page for. */
  function paintLine(root, loc, rate, fmt, sel) {
    var lead  = root.querySelector(sel.lead);
    var place = root.querySelector(sel.place);
    var price = root.querySelector(sel.price);

    if (lead) lead.textContent = loc.uncertain ? 'Est. delivery to' : 'Delivery to';

    if (place) {
      var label = placeLabel(loc);
      place.textContent = label;
      /* Visible text alone stops explaining the control once it reads
         "Brisbane, 4000" instead of "Select your postcode". The label goes on
         the BUTTON -- `place` is now the text span inside it, so that the
         chevron beside it is never touched by the repaint. */
      var btn = place.closest('button');
      if (btn) btn.setAttribute('aria-label', 'Change delivery postcode, currently ' + label);
    }

    if (price && rate) {
      price.textContent = rate.amount === 0 ? 'FREE Shipping' : fmt(rate.amount);
      price.classList.toggle('is-free', rate.amount === 0);
    }
  }

  /* Store format: $1,234 with a bare .00 dropped, matching co-ship-calc.js. */
  function moneyStore(n) {
    var s = '$' + n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return s.replace(/\.00$/, '');
  }
  /* ON:PACE always carries the cents. */
  function moneyOnpace(n) {
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function prefill(root, sel, loc) {
    var state = root.querySelector(sel.state);
    var zip   = root.querySelector(sel.zip);
    var city  = root.querySelector(sel.city);
    if (state && !state.value && loc.state)  state.value = loc.state;
    if (zip   && !zip.value)                 zip.value   = loc.postcode;
    if (city  && !city.value && loc.suburb)  city.value  = loc.suburb;
  }

  /* A postcode the customer typed outranks anything we inferred. The
     existing calculators are left completely alone -- this listens after
     their handler rather than replacing it, so the manual path keeps
     working exactly as it does today. */
  function rememberManualEntry(root, sel, onSet) {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest(sel.calc);
      if (!btn || !root.contains(btn)) return;
      var zip = root.querySelector(sel.zip);
      if (!zip || !validPostcode(zip.value.trim())) return;
      var state = root.querySelector(sel.state);
      var city  = root.querySelector(sel.city);
      var settled = setLocation({
        postcode: zip.value.trim(),
        state: state ? state.value : '',
        suburb: city ? city.value.trim() : ''
      });
      /* Repaint straight away. The panel's own Calculate still runs and fills
         the panel with the full rate list; this keeps the summary line, which
         stays visible above it, from contradicting what they just typed. */
      if (settled && onSet) onSet(settled);
    });
  }

  function bind(opts) {
    var root = document.querySelector(opts.root);
    if (!root) return;
    var label = root.querySelector(opts.label);
    if (!label) return;

    rememberManualEntry(root, opts.sel, function (loc) { run(loc); });
    if (!window.CosShipAuto.enabled) return;

    /* Fast path for a repeat view. Everything needed is already in
       storage, so sitting out the idle timer to redraw a known answer
       just puts a visible beat between the page loading and the price
       appearing -- and someone browsing a category hits this on every
       product they open. Two synchronous storage reads, and the paint
       is the same in-place text swap, so it stays free of layout shift.
       The idle pass still runs afterwards and refreshes if anything has
       changed. */
    (function paintFromCache() {
      var loc = readJSON('localStorage', LOC_KEY, LOC_TTL);
      if (!loc || !validPostcode(loc.postcode)) return;
      var variantId = currentVariantId(opts.form, root);
      var cached = variantId
        ? readJSON('sessionStorage', rateKey(variantId, loc.postcode), RATE_TTL)
        : null;
      paintLine(root, loc, (cached && cached.amount !== null) ? cached : null,
                opts.money, opts.sel);
    })();

    function run(loc) {
      /* Place first, price when it lands. The location is resolved in
         milliseconds and the carrier takes seconds, so showing the suburb
         immediately turns a 3s wait into a line that is already half true. */
      paintLine(root, loc, null, opts.money, opts.sel);
      var variantId = currentVariantId(opts.form, root);
      if (!variantId) return;
      quote(variantId, loc).then(function (rate) {
        /* null = the carriers returned nothing for this item. The place name
           stays, the price stays at its resting value -- no error thrown at
           someone who never asked a question. */
        if (rate) paintLine(root, loc, rate, opts.money, opts.sel);
      }).catch(function () { /* stay silent on the automatic path */ });
    }

    onIdle(function () {
      resolveLocation().then(function (loc) {
        if (!loc) return;
        prefill(root, opts.sel, loc);
        run(loc);
        onVariantChange(opts.form, root, function () {
          resolveLocation().then(function (l) { if (l) run(l); });
        });
      });
    });
  }

  window.CosShipAuto = {
    enabled: !!(CFG && CFG.token),
    config: CFG,
    resolveLocation: resolveLocation,
    setLocation: setLocation,
    quote: quote,
    placeLabel: placeLabel,
    onIdle: onIdle,
    currentVariantId: currentVariantId,
    onVariantChange: onVariantChange
  };

  /* The store PDP block. `:not([data-cosc-cart])` matters -- the cart
     drawer renders two more [data-cosc] roots into every page, they come
     first in the DOM, and they quote the whole cart rather than a
     product. */
  bind({
    root: '[data-cosc]:not([data-cosc-cart])',
    label: '[data-cosc-auto-label]',
    form: '[data-pdpc-id]',
    money: moneyStore,
    sel: { state: '[data-cosc-state]', zip: '[data-cosc-zip]',
           city: '[data-cosc-city]', calc: '[data-cosc-calc]',
           lead: '[data-cosc-lead]', place: '[data-cosc-place]',
           price: '[data-cosc-price]' }
  });

  /* The ON:PACE buy column. */
  bind({
    root: '[data-op-ship]',
    label: '[data-op-ship-auto-label]',
    form: '#op-form [name="id"]',
    money: moneyOnpace,
    sel: { state: '[data-op-ship-state]', zip: '[data-op-ship-zip]',
           city: '[data-op-ship-city]', calc: '[data-op-ship-calc]',
           lead: '[data-op-ship-lead]', place: '[data-op-ship-place]',
           price: '[data-op-ship-price]' }
  });
})();
